import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { NavLink, useLocation } from 'react-router-dom';
import './Sidebar.scss';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from '../../context/BreakpointContext.tsx';
import { getLanguageKeyByType } from '../../utils/getLanguageKeyByType.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { UserRole } from '../../constants/UserRoles.ts';

function useIsMultilineTruncated(
  ref: React.RefObject<HTMLElement | null>,
  text: string,
) {
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      requestAnimationFrame(() => {
        const node = ref.current;
        if (!node) return;
        setTruncated(node.scrollHeight > node.clientHeight + 1);
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [text, ref]);

  return truncated;
}

const SidebarTruncatedLinkText: React.FC<{
  text: string;
  lightTooltip?: boolean;
}> = ({ text, lightTooltip = false }) => {
  const { isMobile } = useBreakpoint();
  const textRef = useRef<HTMLElement | null>(null);
  const isTruncated = useIsMultilineTruncated(textRef, text);

  const trigger = (
    <Box
      as="span"
      display="block"
      flex="1"
      minWidth={0}
      width="100%"
      pointerEvents="auto"
    >
      <Text
        ref={textRef}
        as="span"
        display="-webkit-box"
        width="100%"
        noOfLines={2}
        overflow="hidden"
        sx={{ WebkitBoxOrient: 'vertical' }}
      >
        {text}
      </Text>
    </Box>
  );

  if (isMobile || !isTruncated) {
    return trigger;
  }

  return (
    <Tooltip
      label={text}
      placement="right"
      hasArrow
      openDelay={300}
      bg={lightTooltip ? 'white' : 'alea.500'}
      color={lightTooltip ? 'alea.500' : 'white'}
    >
      {trigger}
    </Tooltip>
  );
};

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const { isMobile } = useBreakpoint();

  const getDisabledLinkProps = (isDisabled: boolean) => {
    if (!isDisabled) return {};
    return {
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault(),
      style: { pointerEvents: 'none' as const },
      tabIndex: -1,
      'aria-disabled': true,
    };
  };

  const isSameOrSubPath = (basePath: string) => {
    if (location.pathname === basePath) return true;
    return location.pathname.startsWith(`${basePath}/`);
  };

  return (
    <Box
      width={isMobile ? 'auto' : '325px'}
      borderRadius="20px"
      ml={isMobile ? '0' : '15px'}
      className="sidebar"
    >
      <Flex flexFlow="column">
        {user?.role === UserRole.CAREHOME_MANAGER && user?.carehome?.name ? (
          // Display care home name when user is a care home manager
          <NavLink
            to="/care-home"
            {...getDisabledLinkProps(location.pathname === '/care-home')}
            className={({ isActive }) =>
              `sidebar-link care-home ${isActive ? 'active' : ''}`
            }
          >
            <SidebarTruncatedLinkText
              text={user.carehome.name}
              lightTooltip={location.pathname === '/care-home'}
            />
          </NavLink>
        ) : user?.organisation?.id ? (
          // Display organisation name link depending on the user role
          <NavLink
            to={
              user?.role === UserRole.ORGANISATION_MANAGER
                ? '/organisation'
                : '/care-home-list'
            }
            {...getDisabledLinkProps(
              location.pathname ===
                (user?.role === UserRole.ORGANISATION_MANAGER
                  ? '/organisation'
                  : '/care-home-list')
            )}
            className={({ isActive }) =>
              `sidebar-link care-home ${isActive || (user?.role !== UserRole.ORGANISATION_MANAGER && location.pathname.startsWith('/care-home/')) ? 'active' : ''}`
            }
          >
            {user?.role === UserRole.ORGANISATION_MANAGER ? (
              <SidebarTruncatedLinkText
                text={user.organisation.name}
                lightTooltip={location.pathname === '/organisation'}
              />
            ) : (
              t('careHomeList')
            )}
          </NavLink>
        ) : (
          // Display single care home link when no organization ID and not a care home manager
          <NavLink
            to="/care-home"
            {...getDisabledLinkProps(location.pathname === '/care-home')}
            className={({ isActive }) =>
              `sidebar-link care-home ${isActive ? 'active' : ''}`
            }
          >
            {t(getLanguageKeyByType('careHome', user?.carehome?.type))}
          </NavLink>
        )}

        <Flex flexFlow="column" backgroundColor="white" borderRadius="20px">
          {user?.role === UserRole.ORGANISATION_MANAGER && (
            <NavLink
              to="/care-home-list"
              {...getDisabledLinkProps(location.pathname === '/care-home-list')}
              className={({ isActive }) =>
                `sidebar-link group-first-item ${isActive || location.pathname.startsWith('/care-home/') ? 'active' : ''}`
              }
            >
              {t('facilities')}
            </NavLink>
          )}
          <NavLink
            to="/residents"
            {...getDisabledLinkProps(location.pathname === '/residents')}
            className={({ isActive }) =>
              `sidebar-link ${user?.role === UserRole.ORGANISATION_MANAGER ? 'group-middle-item' : 'group-first-item'} ${isActive || location.pathname === '/' ? 'active' : ''}`
            }
          >
            {t(getLanguageKeyByType('residents', user?.carehome?.type))}
          </NavLink>
          <NavLink
            to="/app-accounts"
            {...getDisabledLinkProps(location.pathname === '/app-accounts')}
            className={({ isActive }) =>
              `sidebar-link group-middle-item ${isActive ? 'active' : undefined}`
            }
          >
            {t('appAccounts')}
          </NavLink>
          {user?.carehome?.export_params?.is_active === true && (
            <NavLink
              to="/caregivers"
              {...getDisabledLinkProps(location.pathname === '/caregivers')}
              className={({ isActive }) =>
                `sidebar-link group-middle-item ${isActive ? 'active' : undefined}`
              }
            >
              {t('careGivers')}
            </NavLink>
          )}
          <NavLink
            to="/hardware"
            {...getDisabledLinkProps(isSameOrSubPath('/hardware'))}
            className={({ isActive }) =>
              `sidebar-link group-middle-item ${isActive ? 'active' : undefined}`
            }
          >
            {t('hardware')}
          </NavLink>
          <NavLink
            to="/analysis"
            {...getDisabledLinkProps(location.pathname === '/analysis')}
            className={({ isActive }) =>
              `sidebar-link group-last-item ${isActive ? 'active' : undefined}`
            }
          >
            {t('general.analyse')}
          </NavLink>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Sidebar;
